import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { useAppShell } from "../components/layout/AppShell";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { djangoApi } from "../services/djangoApi";

// Status indicator component
function StatusIndicator({ status }) {
  if (status === "pending" || status === "processing") {
    return (
      <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    );
  }
  if (status === "completed") {
    return <span className="text-xs font-bold text-green-600">&#10003;</span>;
  }
  if (status === "failed") {
    return <span className="text-xs font-bold text-red-600">&#10007;</span>;
  }
  return null;
}

function formatTimestamp(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString();
}

export default function ResultsPage({ apiKey }) {
  const location = useLocation();
  const { selectedOrgId } = useAppShell();
  const [processedStatus, setProcessedStatus] = useState("idle");
  const [processedError, setProcessedError] = useState("");
  const [processed, setProcessed] = useState([]);
  const [selectedUlid, setSelectedUlid] = useState("");
  const [detailStatus, setDetailStatus] = useState("idle");
  const [detailError, setDetailError] = useState("");
  const [response, setResponse] = useState(null);
  const autoRefreshRef = useRef(null);

  const requestedUlid = location.state?.ulid;

  // Check if there are pending tasks
  const hasPendingTasks = useMemo(
    () => processed.some((item) => item.status === "pending" || item.status === "processing"),
    [processed]
  );

  async function refreshProcessed() {
    if (!apiKey) return;
    setProcessedError("");
    setProcessedStatus("loading");
    try {
      const data = await djangoApi.listProcessed(apiKey, selectedOrgId, 25);
      const items = Array.isArray(data?.results) ? data.results : [];
      setProcessed(items);
      setProcessedStatus("ready");
      if (requestedUlid && items.some((item) => item.ulid === requestedUlid)) {
        setSelectedUlid(requestedUlid);
      } else if (!selectedUlid && items[0]?.ulid) {
        setSelectedUlid(items[0].ulid);
      }
    } catch (e) {
      setProcessedStatus("error");
      setProcessedError(
        e instanceof Error ? e.message : "Failed to load processed results",
      );
    }
  }

  useEffect(() => {
    if (!apiKey) return;
    refreshProcessed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, selectedOrgId]);

  // Auto-refresh every 2 seconds when there are pending tasks
  useEffect(() => {
    if (!hasPendingTasks || !apiKey) {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
        autoRefreshRef.current = null;
      }
      return;
    }

    autoRefreshRef.current = setInterval(async () => {
      try {
        const data = await djangoApi.listProcessed(apiKey, selectedOrgId, 25);
        const items = Array.isArray(data?.results) ? data.results : [];
        setProcessed(items);
      } catch {
        // Silently fail on auto-refresh errors
      }
    }, 2000);

    return () => {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
        autoRefreshRef.current = null;
      }
    };
  }, [hasPendingTasks, apiKey, selectedOrgId]);

  useEffect(() => {
    if (!apiKey || !selectedUlid) return;
    let isMounted = true;
    async function loadSelected() {
      setDetailError("");
      setDetailStatus("loading");
      try {
        const data = await djangoApi.getProcessedDetail(
          apiKey,
          selectedOrgId,
          selectedUlid,
        );
        if (!isMounted) return;
        setResponse(data);
        setDetailStatus("ready");
      } catch (e) {
        if (!isMounted) return;
        setDetailStatus("error");
        setDetailError(
          e instanceof Error ? e.message : "Failed to load selected item",
        );
      }
    }
    loadSelected();
    return () => {
      isMounted = false;
    };
  }, [apiKey, selectedOrgId, selectedUlid]);

  const selectedItem = useMemo(
    () => processed.find((item) => item.ulid === selectedUlid),
    [processed, selectedUlid],
  );

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <CardTitle>History</CardTitle>
              {hasPendingTasks && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-foreground" />
                  Auto-refreshing
                </span>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={refreshProcessed}
              disabled={processedStatus === "loading"}
            >
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {processedError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              {processedError}
            </div>
          ) : null}

          {processed.length === 0 && processedStatus !== "loading" ? (
            <div className="text-sm text-muted-foreground">
              No runs.
            </div>
          ) : null}

          <div className="max-h-[620px] space-y-2 overflow-auto pr-1">
            {processed.map((item) => {
              const isSelected = item.ulid === selectedUlid;
              const subtitle = `${item.model || "unknown"} · ${formatTimestamp(
                item.created_date,
              )}`;
              return (
                <button
                  key={item.ulid}
                  type="button"
                  onClick={() => setSelectedUlid(item.ulid)}
                  className={[
                    "w-full rounded-lg border p-3 text-left text-sm transition-colors",
                    isSelected
                      ? "border-foreground bg-accent"
                      : "border-border bg-background hover:bg-accent",
                  ].join(" ")}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <StatusIndicator status={item.status} />
                      <div className="font-mono text-xs font-medium">{item.ulid}</div>
                    </div>
                    <div className="flex gap-2">
                      {item.json_file ? (
                        <a
                          className="text-xs font-semibold text-primary hover:underline"
                          href={item.json_file}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          JSON
                        </a>
                      ) : null}
                      {item.pdf_file ? (
                        <a
                          className="text-xs font-semibold text-primary hover:underline"
                          href={item.pdf_file}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          PDF
                        </a>
                      ) : null}
                    </div>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground truncate">
                    {subtitle}
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card className="h-fit">
        <CardHeader>
          <CardTitle>
            {selectedItem ? "Result" : "Select a run"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {detailError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              {detailError}
            </div>
          ) : null}
          {detailStatus === "loading" ? (
            <div className="text-sm text-muted-foreground">
              Loading...
            </div>
          ) : null}
          {response ? (
            <pre className="max-h-[520px] overflow-auto rounded-md border bg-muted p-3 text-xs">
              {typeof response === "string"
                ? response
                : JSON.stringify(response, null, 2)}
            </pre>
          ) : (
            <div className="text-sm text-muted-foreground">
              Select a run to view details.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
